import type { PetAttribute, PetStatusEffect } from '.';

import { SB } from '@play-co/replicant';
import { tuple } from '../utils/types';
import {
  attributeBounds,
  evolutionSequence,
  petAttributes,
  petStatusEffects,
  species,
} from './pet.ruleset';

type AttributeSchema = typeof attributeSchema;
type AttributeEffectSchema = typeof attributeEffectSchema;
type StatusEffectSchema = typeof statusEffectSchema;

const attributeSchema = SB.object({
  value: SB.number().min(attributeBounds.min),
  lastUpdatedAt: SB.number(),
});

/**
 * Temporary buff or debuff attribute effect decay speed
 */
export const attributeEffectSchema = SB.object({
  duration: SB.number(), // How long effect take time
  decay: SB.number(), // How we change decay,  -2 slow attribute decrease, 2 speedup attribute decrease
  startAt: SB.number(), // From what time we start apply it
});

const statusEffectSchema = SB.object({
  value: SB.boolean(),
  lastUpdatedAt: SB.number(),
  expiresAt: SB.number().optional(),
  resolutionAttempts: SB.number().default(0),
});

export const petSchema = {
  petRaised: SB.number(),
  highestPetAge: SB.number(),
  pet: SB.object({
    name: SB.string(),
    nameUpdated: SB.boolean().default(true),

    evolutions: SB.array(
      SB.object({
        species: SB.tuple(tuple(...species)),
        stage: SB.tuple(evolutionSequence),
        timestamp: SB.number(),
      }),
    ),
    attributes: SB.object(
      petAttributes.reduce(
        (schema, attribute) => ({
          ...schema,
          [attribute]: attributeSchema,
        }),
        {} as Record<PetAttribute, AttributeSchema>,
      ),
    ),
    // Temporary buff or debuff attribute effect decay speed
    attributesEffects: SB.object(
      petAttributes.reduce(
        (schema, attribute) => ({
          ...schema,
          [attribute]: attributeEffectSchema,
        }),
        {} as Record<PetAttribute, AttributeEffectSchema>,
      ),
    ),
    statusEffects: SB.object(
      petStatusEffects.reduce(
        (schema, effect) => ({
          ...schema,
          [effect]: statusEffectSchema,
        }),
        {} as Record<PetStatusEffect, StatusEffectSchema>,
      ),
    ),
    /**
     * A/B 0468 V2: timestamp of the last wantToInvite fire (false → true
     * transition). 0 = never fired. Used by V2 invite gate to distinguish
     * first-nudge from recurrence path WITHOUT touching `lastUpdatedAt`,
     * which `syncPermanentStatusEffects` can write speculatively before any
     * real fire and would otherwise pollute the sentinel.
     */
    lastWantToInviteFireAt: SB.number().default(0),
  }),

  evolutionRewind: SB.object({
    count: SB.int().min(0),
    species: SB.string(),
    stage: SB.string(),
  }).optional(),
};
